import { useCallback, useEffect, useRef, useState } from 'react';

type UseFitTextProps = {
  textRef: React.RefObject<HTMLDivElement>;
  maxFontSize: number;
  widthOffset: number;
  heightOffset: number;
  minFontSize?: number;
};

/**
 * Custom hook to automatically adjust the font size of a text element so that it fits within its container.
 *
 * @param {Object} props - The properties for the hook.
 * @param {React.RefObject<HTMLDivElement>} props.textRef - Ref to the text element whose font size should be adjusted.
 * @param {number} props.maxFontSize - The maximum font size to use.
 * @param {number} props.widthOffset - The total horizontal offset (e.g., padding/margin) to subtract from the container's width.
 * @param {number} props.heightOffset - The total vertical offset (e.g., padding/margin) to subtract from the container's height.
 * @param {number} [props.minFontSize=10] - The minimum font size to use.
 *
 * @returns {{
 *   containerRef: React.RefObject<HTMLDivElement>,
 *   fontSize: number,
 *   lineCount: number
 * }} An object containing a ref to be attached to the container, the calculated font size, and the number of text lines.
 *
 * @example
 * const { containerRef, fontSize } = useFitText({
 *   textRef,
 *   maxFontSize: 100,
 *   widthOffset: 40,
 *   heightOffset: 20,
 * });
 *
 * return (
 *   <div ref={containerRef}>
 *     <div ref={textRef} style={{ fontSize }}>
 *       Some text
 *     </div>
 *   </div>
 * );
 */

export const useFitText = (
  args: UseFitTextProps,
): {
  containerRef: React.RefObject<HTMLDivElement>;
  fontSize: number;
  lineCount: number;
} => {
  const { textRef, maxFontSize, widthOffset, heightOffset, minFontSize = 10 } = args;

  const [fontMetrics, setFontMetrics] = useState({ fontSize: maxFontSize, lineCount: 1 });

  const containerRef = useRef<HTMLDivElement>(null);

  const calculateFontSize = useCallback(() => {
    if (!containerRef.current || !textRef.current) return;
    let currentSize = maxFontSize;

    const containerEl = containerRef.current;
    const textEl = textRef.current;

    // Reset font size before measuring
    textEl.style.fontSize = `${currentSize}px`;

    const maxTextContainerHeight = containerEl.clientHeight - heightOffset;
    const maxTextContainerWidth = containerEl.clientWidth - widthOffset;

    while (true) {
      const textOverflowsVertically = textEl.scrollHeight > maxTextContainerHeight;
      const textOverflowsHorizontally = textEl.scrollWidth > maxTextContainerWidth;
      const textOverflows = textOverflowsVertically || textOverflowsHorizontally;
      const fontBecomesTooSmall = currentSize <= minFontSize;

      if (!textOverflows || fontBecomesTooSmall) break;

      currentSize -= 1;
      textEl.style.fontSize = `${currentSize}px`;
    }

    // the height of a line of the text. 1.25 is the "line-height" value.
    const lineHeight = currentSize * 1.25;
    // the number of lines of the text. min 1 line.
    const lines = Math.max(1, Math.round(textEl.scrollHeight / lineHeight));

    setFontMetrics({ fontSize: currentSize, lineCount: lines });
  }, [heightOffset, maxFontSize, minFontSize, textRef, widthOffset]);

  useEffect(() => {
    calculateFontSize();

    // Watch container size changes
    // eslint-disable-next-line compat/compat
    const resizeObserver = new ResizeObserver(() => calculateFontSize());
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Watch text content changes
    const mutationObserver = new MutationObserver(() => calculateFontSize());
    if (textRef.current) {
      mutationObserver.observe(textRef.current, {
        characterData: true,
        childList: true,
        subtree: true,
      });
    }

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [calculateFontSize, textRef]);

  return { containerRef, fontSize: fontMetrics.fontSize, lineCount: fontMetrics.lineCount };
};
